import * as cornerstone from '@cornerstonejs/core';

const SCHEME = 'web';

function getPixelData(imageData: ImageData): Uint8Array {
  const rgba = imageData.data;
  const pixelCount = imageData.width * imageData.height;
  const rgb = new Uint8Array(pixelCount * 3);

  for (let i = 0; i < pixelCount; i += 1) {
    rgb[i * 3] = rgba[i * 4];
    rgb[i * 3 + 1] = rgba[i * 4 + 1];
    rgb[i * 3 + 2] = rgba[i * 4 + 2];
  }

  return rgb;
}

function loadImage(imageId: string): {
  promise: Promise<cornerstone.Types.IImage>;
} {
  const imageUrl = imageId.replace(`${SCHEME}:`, '');

  const promise = new Promise<cornerstone.Types.IImage>((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      const pixelData = getPixelData(imageData);

      // Use Partial<IImage> and cast to handle optional fields that aren't needed for web images
      const image = {
        imageId,
        minPixelValue: 0,
        maxPixelValue: 255,
        slope: 1,
        intercept: 0,
        windowCenter: 128,
        windowWidth: 256,
        getPixelData: () => pixelData,
        rows: img.height,
        columns: img.width,
        height: img.height,
        width: img.width,
        color: true,
        rgba: false,
        columnPixelSpacing: 1,
        rowPixelSpacing: 1,
        invert: false,
        sizeInBytes: pixelData.byteLength,
        photometricInterpretation: 'RGB',
        numberOfComponents: 3,
        voiLUTFunction: undefined,
        getCanvas: undefined,
        dataType: undefined,
      } as unknown as cornerstone.Types.IImage;

      // CLEANUP: Release temporary resources to prevent memory accumulation
      // Without this, 16 viewports × 100 frames = 1,600 canvas elements in memory
      canvas.width = 0;
      canvas.height = 0;
      img.src = ''; // Release image memory
      img.onload = null;
      img.onerror = null;

      resolve(image);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    if (!imageUrl.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.src = imageUrl;
  });

  return { promise };
}

export function registerWebImageLoader(): void {
  cornerstone.imageLoader.registerImageLoader(SCHEME, loadImage);
}
